<script setup lang="ts">
/** BON02 — Letter of Application for Shares (청약자 → 회사). */
import { computed, inject } from 'vue';
import { BONUS_DOC_CTX } from './bonusContext';
import BonusDocVarField from './BonusDocVarField.vue';
import BonusSigners from './parts/BonusSigners.vue';

const ctx = inject(BONUS_DOC_CTX)!;
const hasConstitution = computed(() => ctx.flag('has_constitution'));
const isPolitician = computed(() => ctx.flag('is_politician'));
const isPep = computed(() => ctx.flag('is_pep_connected'));
const holdsGovt = computed(() => ctx.flag('holds_govt_position'));
const isTrustee = computed(() => ctx.flag('is_trustee'));
</script>

<template>
  <div class="allot-doc">
    <p class="doc-p" style="text-align:right"><BonusDocVarField field-key="letter_date" /></p>
    <p class="doc-p">The Board of Directors<br /><BonusDocVarField field-key="company_name" /><br /><BonusDocVarField field-key="company_address" /></p>

    <p class="doc-sec-title">LETTER OF APPLICATION FOR SHARES</p>
    <p class="doc-p">Dear Sir/Madam,</p>
    <p class="doc-p">
      I/We hereby apply for <BonusDocVarField field-key="shares_subscribed" /> ordinary share(s) of
      RM<BonusDocVarField field-key="share_price" /> each in the capital of the Company and request that such shares be
      allotted to me/us subject to the
      <span class="doc-toggle" title="Constitution present?" @click="ctx.toggleFlag('has_constitution')">{{ hasConstitution ? "Constitution of the Company" : "Companies Act 2016" }}</span>.
    </p>

    <p class="doc-lbl-title">PARTICULARS OF APPLICANT</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name</td><td><BonusDocVarField field-key="subscriber_name" /></td></tr>
        <tr><td>NRIC / Passport / Company No.</td><td><BonusDocVarField field-key="subscriber_id" /></td></tr>
        <tr><td>Person / Company</td><td><BonusDocVarField field-key="subscriber_type" /></td></tr>
        <tr><td>Date of Birth</td><td><BonusDocVarField field-key="subscriber_dob" /></td></tr>
        <tr><td>Nationality</td><td><BonusDocVarField field-key="subscriber_nationality" /></td></tr>
        <tr><td>Address</td><td><BonusDocVarField field-key="subscriber_address" /></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">DECLARATIONS</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('is_politician')">{{ isPolitician ? 'I AM' : 'I am NOT' }}</span> a politician / political appointee.
      <template v-if="isPolitician"> Details: <BonusDocVarField field-key="politician_details" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('is_pep_connected')">{{ isPep ? 'I AM' : 'I am NOT' }}</span> connected to a Politically Exposed Person (PEP).
      <template v-if="isPep"> Details: <BonusDocVarField field-key="pep_connection_details" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('holds_govt_position')">{{ holdsGovt ? 'I HOLD' : 'I do NOT hold' }}</span> a government position.
      <template v-if="holdsGovt"> Details: <BonusDocVarField field-key="govt_position_details" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('is_trustee')">{{ isTrustee ? 'I AM' : 'I am NOT' }}</span> holding these shares as a trustee / beneficial owner on behalf of another person.
    </p>

    <template v-if="isTrustee">
      <p class="doc-lbl-title">BENEFICIAL OWNER PARTICULARS</p>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Full Name</td><td><BonusDocVarField field-key="bo_full_name" /></td></tr>
          <tr><td>Nature of Business</td><td><BonusDocVarField field-key="bo_nature" /></td></tr>
          <tr><td>Place of Registration</td><td><BonusDocVarField field-key="bo_place" /></td></tr>
          <tr><td>Registration No.</td><td><BonusDocVarField field-key="bo_registration_no" /></td></tr>
          <tr><td>Registered Address</td><td><BonusDocVarField field-key="bo_registered_address" /></td></tr>
          <tr><td>Business Address</td><td><BonusDocVarField field-key="bo_business_address" /></td></tr>
        </tbody>
      </table>
    </template>

    <p class="doc-p" style="margin-top:12px">
      NUMBER OF EQUITY SHARES OFFERED<br />
      : <BonusDocVarField field-key="shares_offered" /> Ordinary shares
    </p>
    <p class="doc-p">
      NUMBER OF EQUITY SHARES ACCEPTED<br />
      : <BonusDocVarField field-key="shares_accepted" /> Ordinary shares
    </p>

    <p class="doc-p" style="margin-top:12px">I/We agree to accept the same or any smaller number of shares as may be allotted to me/us.</p>
    <BonusSigners var-key="application" />
  </div>
</template>
